import { useImperativeHandle, Fragment, useState, useRef } from 'react';
import type { Ref } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { CommentForm } from 'hb-react/frontoffice/apps/CommentsV2/components/CommentForm';
import { LoadMore } from 'hb-react/frontoffice/apps/CommentsV2/components/LoadMore';
import { SimpleComment } from 'hb-react/frontoffice/apps/CommentsV2/components/SimpleComment';
import { COMMENTABLE_TYPES } from 'hb-react/frontoffice/apps/CommentsV2/constants';
import { useCommentsContext } from 'hb-react/frontoffice/apps/CommentsV2/contexts/CommentsContext';
import { useComments } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useComments';
import { useCommentsQueryHandlers } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useCommentsQueryHandlers';
import { useDeferredFocus } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useDeferredFocus';
import { useRemoveFocusedCommentsSearchParams } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useFocusedCommentSearchParams';
import type {
  Comment,
  CommentableType,
  ReplyMention,
} from 'hb-react/frontoffice/apps/CommentsV2/types';
import { getQueryKey } from 'hb-react/frontoffice/apps/CommentsV2/utils/getQueryKey';
import type { QuillEditorV3Ref } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';

export interface RepliesImperativeHandle {
  focusReplyForm: () => void;
}

interface RepliesProps {
  commentableId: number;
  commentableType: CommentableType;
  currentUser: any;
  hasReplies: boolean;
  onReplyCreated: (attr: { newComment: Comment }) => void;
  repliesRef?: Ref<RepliesImperativeHandle>;
}

export const Replies = ({
  commentableId,
  commentableType,
  currentUser,
  hasReplies,
  onReplyCreated,
  repliesRef,
}: RepliesProps) => {
  const [displayCommentInput, setDisplayCommentInput] = useState(false);
  const [externalMention, setExternalMention] = useState<ReplyMention>();
  const quillRef = useRef<QuillEditorV3Ref>(null);
  const removeCommentsSearchParams = useRemoveFocusedCommentsSearchParams();

  const {
    canDisplayEmojiPicker,
    focusedCommentId,
    focusedReplyId,
    isOpenInModal,
    sortBy,
    options,
  } = useCommentsContext();

  const queryKey = getQueryKey({
    commentableId,
    commentableType,
    sortBy,
    isOpenInModal,
    focusedCommentId,
    focusedReplyId,
  });

  const {
    data,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
  } = useComments({
    queryKey,
    enabled: hasReplies,
    params: {
      commentableId,
      commentableType,
      sortBy,
      focusedCommentId:
        commentableId === focusedCommentId ? focusedReplyId : undefined,
    },
    onFocusedCommentNotFound: () => removeCommentsSearchParams(),
  });

  const {
    handleNewComment,
    handleDeleteComment,
    handleUpdateComment,
    handleToggleLike,
    handleVote,
  } = useCommentsQueryHandlers(queryKey);

  const focusCommentForm = useDeferredFocus(quillRef);

  const hasItems = data && data.pages.some((page) => page.data.length > 0);

  // Expose interaction interface within provided ref
  useImperativeHandle(
    repliesRef,
    () => ({
      focusReplyForm: () => {
        setDisplayCommentInput(true);
        focusCommentForm();
      },
    }),
    [focusCommentForm],
  );

  const handleShowReplyForm = (mentionProps) => {
    // handle on reply clicked
    if (!displayCommentInput) {
      setDisplayCommentInput(true);
    } else {
      focusCommentForm();
    }

    if (mentionProps) {
      setExternalMention(mentionProps);
    }
  };

  const handleReplyCreated = ({ newComment }) => {
    handleNewComment({ newComment });
    onReplyCreated?.({ newComment });
  };

  if (isLoading) {
    return (
      <Box py={6}>
        <Loader size={15} />
      </Box>
    );
  }

  return (
    <>
      {hasItems && (
        <Box flexDirection="column" mb={3} pl={16}>
          {hasNextPage ? (
            <Box justifyContent="flex-start" mb={2}>
              <LoadMore
                isFetching={isFetchingNextPage}
                loadMore={fetchNextPage}
                loadingText={s__('Comments|Loading...')}
                linkText={s__('Replies|See more replies')}
              />
            </Box>
          ) : null}

          {/* IMPORTANT: Renders in a reverse order via CSS!!! */}
          <RepliesWrapper flexDirection="column-reverse" mt={3} mb={2}>
            {data?.pages.map((page, i) => (
              <Fragment key={`${page.start_cursor}-${page.end_cursor}`}>
                {page.data.map((comment, j) => (
                  <Box
                    key={comment.id}
                    data-testid="comment-reply"
                    flexDirection="column"
                    // First element will be on the bottom - no margin.
                    mb={i === 0 && j === 0 ? 0 : 3}
                  >
                    <SimpleComment
                      canDisplayEmojiPicker={canDisplayEmojiPicker}
                      comment={comment}
                      currentUser={currentUser}
                      isFocused={focusedReplyId === comment.id}
                      isReply
                      onDeleteComment={handleDeleteComment}
                      onReply={handleShowReplyForm}
                      onToggleLike={handleToggleLike}
                      onUpdateComment={handleUpdateComment}
                      onVote={handleVote}
                      options={options}
                    />
                  </Box>
                ))}
              </Fragment>
            ))}
          </RepliesWrapper>

          {hasPreviousPage ? (
            <Box justifyContent="flex-start" my={1}>
              <LoadMore
                isFetching={isFetchingPreviousPage}
                loadMore={fetchPreviousPage}
                loadingText={s__('Comments|Loading...')}
                linkText={s__('Replies|See more replies')}
              />
            </Box>
          ) : null}
        </Box>
      )}

      {displayCommentInput && (
        <Box flexDirection="column" pl={16}>
          <CommentForm
            // @ts-expect-error untyped component
            autoFocus
            canDisplayEmojiPicker={canDisplayEmojiPicker}
            commentableId={commentableId}
            commentableType={COMMENTABLE_TYPES.COMMENT}
            commentId={commentableId}
            externalMention={externalMention}
            handleOnFocus={focusCommentForm}
            isReply
            onComment={handleReplyCreated}
            ref={quillRef}
            user={currentUser}
          />
        </Box>
      )}
    </>
  );
};

const RepliesWrapper = styled(Box)`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: -${({ theme }) => theme.space[6]}px;
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.horizon};
  }
`;
